import React, {useState} from "react";
import {useAuthInfo} from "@propelauth/react";
import {Check, EyeOff, ListPlus, Plus, Trash2} from "lucide-react";
import {Plug, PlugKind, PlugsView} from "../bid/bidding_model";
import {Popover, PopoverContent, PopoverTrigger} from "../ui/popover";
import {Input} from "../ui/input";
import {Button} from "../ui/button";
import {useToast} from "../ui/use-toast";
import {formatDate, moneyFormatter} from "../../lib/formatters";
import {
    addPlug, deletePlug, DEV_LINE, hasAmount, isCounted, kindLabel, kindStyle, parseAmount, PLUG_KINDS, plugSum,
    plugToLine, signedMoney, STALE_PLUG, STALE_TITLE, updatePlug
} from "./plugs";


// marks a developer only line - template editor, bid page, compare
export function DevLineTag() {
    return <span className={"inline-flex items-center rounded px-1 py-0.5 " + DEV_LINE.tag}
                 title="Developer only - bidders don't see this line"
                 aria-label="Developer only">
        <EyeOff className="w-3 h-3" aria-hidden="true"/>
    </span>
}


type PlugPopoverProps = {
    project_id: string;
    bid_id: string;
    // SOV input cell id, or null for the base bid
    cell_id: number | null;
    plugs: Plug[];
    // the sub's value on this line - shows the leveled line in the popover
    subValue?: number | null;
    onChange: (view: PlugsView) => void;
    // convert to line changed the SOV - reload it
    onConverted?: () => void;
    // a bidder's text cell - plugs there are notes, always INFO (backend plug_service.is_text_cell)
    infoOnly?: boolean;
    // what opens the popover - a button (needs a ref)
    children: React.ReactElement;
}


// "+" trigger - forwardRef so the popover can anchor to it
export const PlugAddButton = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
    (props, ref) =>
        <button ref={ref} type="button" aria-label="Add plug"
                className="rounded px-0.5 text-gray-700 hover:text-black hover:bg-gray-200" {...props}>
            <Plus className="w-3 h-3"/>
        </button>
);
PlugAddButton.displayName = "PlugAddButton";


export function KindSelect({value, onChange, disabled}: {
    value: PlugKind,
    onChange: (kind: PlugKind) => void,
    disabled?: boolean
}) {
    return <select className={"h-7 rounded-md border border-input px-1 text-xs " + kindStyle(value).tag}
                   value={value} disabled={disabled} onChange={(e) => onChange(e.target.value as PlugKind)}>
        {PLUG_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
    </select>
}


/**
 * One existing plug - amount and note save on blur, kind on change.
 * a stale plug (carried from the previous bid) has a check button in front - it clears the flag
 * */
function PlugRow({plug, onSave, onDelete, onConvert, checkSlot, kindLocked}: {
    plug: Plug,
    onSave: (data: { amount?: number, note?: string, kind?: PlugKind, stale?: boolean }) => void,
    onDelete: () => void,
    // null for base bid plugs - they have no line
    onConvert: (() => void) | null,
    // a plug in the popover is stale - keep a column for the check button, so the rows line up
    checkSlot: boolean,
    // a bidder's text cell - always INFO
    kindLocked: boolean
}) {
    const [amount, setAmount] = useState(String(plug.amount));
    const [note, setNote] = useState(plug.note);

    const saveAmount = () => {
        const value = parseAmount(amount);
        if (value === null || value === plug.amount) {
            setAmount(String(plug.amount));
            return;
        }
        onSave({amount: value});
    }

    const saveNote = () => {
        if (note !== plug.note) onSave({note});
    }

    return <div className="py-1.5 border-b border-gray-100">
        <div className="flex gap-1 items-center">
            {checkSlot && (plug.stale
                ? <Button variant="ghost" size="sm" className={"h-7 w-7 shrink-0 px-0 text-red-600 " + STALE_PLUG}
                          onClick={() => onSave({stale: false})}
                          title="Mark checked - this plug was carried from the previous bid"
                          aria-label="Mark plug checked">
                    <Check className="w-3 h-3"/>
                </Button>
                : <span className="w-7 shrink-0"/>)}
            <KindSelect value={plug.kind ?? "add"} onChange={(kind) => onSave({kind})} disabled={kindLocked}/>
            {/* included / excluded have no amount - the space is kept so the notes line up */}
            {hasAmount(plug.kind)
                ? <Input className="h-7 w-24 shrink-0 text-xs text-right" value={amount}
                         onChange={(e) => setAmount(e.target.value)} onBlur={saveAmount}/>
                : <span className="w-24 shrink-0"/>}
            <Input className="h-7 text-xs" value={note} placeholder="Note"
                   onChange={(e) => setNote(e.target.value)} onBlur={saveNote}/>
            {onConvert &&
                <Button variant="ghost" size="sm" className="h-7 px-2" onClick={onConvert}
                        title="Convert to SOV line" aria-label="Convert to SOV line">
                    <ListPlus className="w-3 h-3"/>
                </Button>}
            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={onDelete} aria-label="Delete plug">
                <Trash2 className="w-3 h-3"/>
            </Button>
        </div>
        <div className="text-[10px] text-gray-400 mt-0.5">
            {plug.stale && <span className="text-red-600">carried from the previous bid · </span>}
            {!isCounted(plug) && "not in total · "}
            {plug.source === "ai" ? "AI · " : ""}{plug.updated_by} · {formatDate(plug.updated_at)}
        </div>
    </div>
}


/**
 * Plugs on one SOV line (or the base bid) of one bid - a popover to add / edit / delete / convert to line.
 * Developer only. Every change is saved on its own - see backend/leveling/leveling_routes.py
 * */
export function PlugPopover({project_id, bid_id, cell_id, plugs, subValue, onChange, onConverted, infoOnly,
                                children}: PlugPopoverProps) {
    const {accessToken} = useAuthInfo();
    const {toast} = useToast();
    // 0 to start - a note only plug needs no amount typed
    const [amount, setAmount] = useState("0");
    const [note, setNote] = useState("");
    const [kind, setKind] = useState<PlugKind>(infoOnly ? "info" : "add");
    const total = plugSum(plugs);
    const anyStale = plugs.some((p) => p.stale);

    // true if saved
    const run = (request: Promise<PlugsView>) => request
        .then((view) => {
            onChange(view);
            return true;
        })
        .catch((err) => {
            toast({title: "Error", description: "Plug not saved: " + err.message, variant: "destructive"});
            return false;
        });

    const onAdd = () => {
        const value = hasAmount(kind) ? parseAmount(amount) : 0;
        if (value === null) {
            toast({title: "Enter an amount", description: "eg 15000 or -5000, or 0", variant: "destructive"});
            return;
        }
        run(addPlug(project_id, bid_id, cell_id, {amount: value, note, kind}, accessToken as string))
            .then((ok) => {
                if (ok) {
                    setAmount("0");
                    setNote("");
                }
            });
    }

    const onConvert = (p: Plug) => {
        run(plugToLine(project_id, bid_id, p.id, accessToken as string))
            .then((ok) => ok && onConverted && onConverted());
    }

    return <Popover>
        <PopoverTrigger asChild>
            {children}
        </PopoverTrigger>
        <PopoverContent className="w-[30rem] p-3 text-xs" align="end">
            <div className="text-gray-500 mb-1">
                {cell_id != null ? "Plugs on this line" : "Base bid plugs"}
                {subValue !== undefined && subValue !== null && ` · sub ${moneyFormatter.format(subValue)}`}
            </div>

            {plugs.map((p) =>
                <PlugRow key={p.id + p.updated_at}
                         plug={p}
                         onSave={(data) => run(updatePlug(project_id, bid_id, p.id, data, accessToken as string))}
                         onDelete={() => run(deletePlug(project_id, bid_id, p.id, accessToken as string))}
                         onConvert={cell_id != null && isCounted(p) ? () => onConvert(p) : null}
                         checkSlot={anyStale}
                         kindLocked={!!infoOnly}/>
            )}

            <div className="flex gap-1 items-center mt-2">
                {anyStale && <span className="w-7 shrink-0"/>}
                <KindSelect value={kind} onChange={setKind} disabled={infoOnly}/>
                {hasAmount(kind)
                    ? <Input className="h-7 w-24 shrink-0 text-xs text-right" value={amount} placeholder="$0"
                             // typing replaces the 0
                             onFocus={(e) => e.target.select()}
                             onChange={(e) => setAmount(e.target.value)}
                             onKeyDown={(e) => e.key === "Enter" && onAdd()}/>
                    : <span className="w-24 shrink-0"/>}
                <Input className="h-7 text-xs" value={note} placeholder="Note"
                       onChange={(e) => setNote(e.target.value)}
                       onKeyDown={(e) => e.key === "Enter" && onAdd()}/>
                <Button variant="outline" size="sm" className="h-7 px-2" onClick={onAdd}>Add</Button>
            </div>

            {/* notes on a text cell add nothing up */}
            {plugs.length > 0 && !infoOnly && <div className="flex justify-between mt-2 font-medium">
                <span>{signedMoney(total)} in total</span>
                {subValue !== undefined &&
                    <span>{moneyFormatter.format((subValue || 0) + total)} leveled</span>}
            </div>}
        </PopoverContent>
    </Popover>
}


/**
 * Compare tab cell: counted total of the line's plugs (or the kind, if none count), or "+" when empty.
 * coloured by the first counted plug's kind.
 * */
export function PlugCell(props: Omit<PlugPopoverProps, "children">) {
    const {plugs} = props;
    const counted = plugs.filter(isCounted);
    const shown = counted.length > 0 ? counted[0] : plugs[0];
    const stale = plugs.some((p) => p.stale);
    return <PlugPopover {...props}>
        {plugs.length > 0
            ? <button type="button" title={stale ? STALE_TITLE : undefined}
                      className={"rounded px-1.5 text-xs whitespace-nowrap hover:opacity-80 " +
                          kindStyle(shown.kind).tag + (stale ? " " + STALE_PLUG : "")}>
                {counted.length > 0 ? signedMoney(plugSum(plugs)) : kindLabel(plugs[0].kind)}
                {plugs.length > 1 ? ` ·${plugs.length}` : ""}
            </button>
            : <PlugAddButton/>}
    </PlugPopover>
}
